import React, { useState } from "react";
import {
  Paper,
  Stack,
  Typography,
  Collapse,
  Box,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BlurLinearIcon from "@mui/icons-material/BlurLinear";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CustomDialogBase } from "../../utils/dialog/customDialog";

const SectionBox = ({
  title,
  children,
  headerActions,
  onAdd,
  defaultExpanded = true,
  unsavedCount = 0,
  totalCounts = 0,
  showUnsavedCount = false,
  savedTerm = "saved",
  showInfoSection = false,
  infoSection = <></>,
  canEdit = true,
}) => {
  const [viewInfo, setViewInfo] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => setExpanded((prev) => !prev);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        "&:hover": {
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      {/* Header */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{
          px: 3,
          py: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
          borderBottom: "1px solid",
          borderColor: "divider",
          cursor: "pointer",
          ...(headerActions ? { flexWrap: "wrap", gap: 1.5 } : {}),
        }}
        onClick={handleToggle}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <BlurLinearIcon color='primary' />
          </Box>
          <Typography
            variant='subtitle1'
            fontSize={18}
            fontWeight={600}
            color='text.primary'
          >
            {title}
          </Typography>
          {showInfoSection && (
            <Tooltip title={title} arrow>
              <InfoOutlinedIcon
                sx={{
                  color: "info.main",
                  fontSize: 20,
                  filter: "drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))",
                  verticalAlign: "middle",
                  ml: 6,
                  mr: 6,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewInfo((prev) => !prev);
                }}
              />
            </Tooltip>
          )}
          {showUnsavedCount && (
            <Chip
              label={
                unsavedCount > 0
                  ? `${unsavedCount} un${savedTerm}${
                      totalCounts ? ` of ${totalCounts}` : ""
                    }`
                  : `All ${savedTerm}`
              }
              size='small'
              color={unsavedCount > 0 ? "warning" : "success"}
              sx={{
                height: 24,
                fontSize: "0.75rem",
                fontWeight: 500,
                "& .MuiChip-label": {
                  px: 1.5,
                },
              }}
            />
          )}
        </Stack>

        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={headerActions ? { ml: "auto", flexShrink: 0 } : undefined}
        >
          {headerActions}
          {canEdit && onAdd && (
            <IconButtonWithTooltip
              icon={AddCircleOutlineIcon}
              tooltipTitle='Add New Row'
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              color='primary'
              size='small'
            />
          )}
          <IconButton
            size='small'
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
              cursor: "pointer",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* Collapsible Content */}
      <Collapse in={expanded}>
        <Box>{children}</Box>
      </Collapse>

      <CustomDialogBase
        open={viewInfo}
        onClose={() => {
          setViewInfo(false);
        }}
        title={title}
        type='info'
      >
        {infoSection}
      </CustomDialogBase>
    </Paper>
  );
};

export default SectionBox;
